/**
 * Send-a-balloon products (variant SKU prefix SAB) cannot share a cart with
 * other products. Only one SAB line is allowed, and its quantity is always 1.
 * Lowering or removing it stays allowed so the cart can be corrected.
 *
 * Installed as a blocking head script so window.fetch is wrapped before
 * product-form and app embeds add to the cart.
 */

const SAB_SKU_PREFIX = 'SAB';

/** @type {Map<string, string>} */
const skuByVariantId = new Map();

/** @type {HTMLFormElement | null} */
let lastAddForm = null;

/** @type {number} */
let lastAddFormAt = 0;

/** @type {Promise<void>} */
let gate = Promise.resolve();

const originalFetch = window.fetch.bind(window);

/**
 * @param {string | null | undefined} sku
 * @returns {boolean}
 */
function isSabSku(sku) {
  return typeof sku === 'string' && sku.trim().toUpperCase().startsWith(SAB_SKU_PREFIX);
}

/**
 * Theme is a global lexical binding from snippets/scripts.liquid, not window.Theme.
 * @returns {{ translations?: { sab_exclusive_cart?: string, sab_quantity_locked?: string, sab_one_product?: string }, routes?: { cart_url?: string }, sabCart?: { variantId: number, key: string, quantity: number, sku: string }[] } | null}
 */
function themeConfig() {
  try {
    return Theme;
  } catch {
    return null;
  }
}

/**
 * @param {'mix' | 'quantity' | 'another'} kind
 * @returns {string}
 */
function blockMessage(kind) {
  const translations = themeConfig()?.translations;
  const message =
    kind === 'quantity'
      ? translations?.sab_quantity_locked
      : kind === 'another'
        ? translations?.sab_one_product
        : translations?.sab_exclusive_cart;

  if (typeof message === 'string' && message.trim()) return message;

  if (kind === 'quantity') return 'Only 1 Send-a-balloon can be added.';
  if (kind === 'another') return 'Only one Send-a-balloon product can be in the cart.';

  return 'Send-a-balloon products cannot be combined with other products.';
}

/**
 * @param {RequestInfo | URL} input
 * @returns {string}
 */
function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (input instanceof Request) return input.url;

  return '';
}

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit} [init]
 * @returns {string}
 */
function requestMethod(input, init) {
  if (init?.method) return init.method.toUpperCase();
  if (input instanceof Request) return input.method.toUpperCase();

  return 'GET';
}

/**
 * @param {string} url
 * @returns {'add' | 'update' | 'change' | null}
 */
function cartMutationKind(url) {
  try {
    const base = window.location?.origin || 'https://shop.local';
    const path = new URL(url, base).pathname;

    if (/\/cart\/add(\.js)?$/.test(path)) return 'add';
    if (/\/cart\/update(\.js)?$/.test(path)) return 'update';
    if (/\/cart\/change(\.js)?$/.test(path)) return 'change';
  } catch {
    return null;
  }

  return null;
}

/**
 * @param {Map<string, number>} quantities
 * @param {string} id
 * @param {number} quantity
 */
function addQuantity(quantities, id, quantity) {
  const variantId = String(id).trim();

  if (!variantId || !Number.isFinite(quantity)) return;

  quantities.set(variantId, (quantities.get(variantId) || 0) + quantity);
}

/**
 * @typedef {Object} IncomingCartChange
 * @property {Map<string, number>} quantities
 * @property {number | null} line
 */

/**
 * @param {Request} request
 * @returns {Promise<IncomingCartChange>}
 */
async function readIncomingChange(request) {
  /** @type {Map<string, number>} */
  const quantities = new Map();
  /** @type {number | null} */
  let line = null;
  const contentType = request.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const data = await request.json();

    if (!data || typeof data !== 'object') return { quantities, line };

    if ('line' in data && data.line != null) {
      const parsedLine = Number(data.line);
      const quantity = Number(data.quantity);

      if (Number.isFinite(parsedLine)) line = parsedLine;
      if (Number.isFinite(parsedLine) && Number.isFinite(quantity)) {
        addQuantity(quantities, `line:${parsedLine}`, quantity);
      }
    }

    if ('id' in data && data.id != null) {
      const quantity = Number(data.quantity);

      addQuantity(quantities, data.id, Number.isFinite(quantity) ? quantity : 1);
    }

    if (Array.isArray(data.items)) {
      for (const item of data.items) {
        if (!item || item.id == null) continue;

        const quantity = Number(item.quantity);

        addQuantity(quantities, item.id, Number.isFinite(quantity) ? quantity : 1);
      }
    }

    if (data.updates && typeof data.updates === 'object') {
      for (const [id, quantity] of Object.entries(data.updates)) {
        addQuantity(quantities, id, Number(quantity) || 0);
      }
    }

    return { quantities, line };
  }

  const form = await request.formData();
  let bareQuantity = null;

  for (const [key, value] of form.entries()) {
    if (typeof value !== 'string') continue;

    if (key === 'line') {
      const parsedLine = Number(value);

      if (Number.isFinite(parsedLine)) line = parsedLine;
      continue;
    }

    if (key === 'quantity') {
      const quantity = Number(value);

      bareQuantity = Number.isFinite(quantity) ? quantity : null;
      continue;
    }

    if (key === 'id') {
      const quantity = Number(form.get('quantity'));

      addQuantity(quantities, value, Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
      continue;
    }

    const itemMatch = key.match(/^items\[(\d+)\]\[id\]$/);

    if (itemMatch) {
      const quantity = Number(form.get(`items[${itemMatch[1]}][quantity]`));

      addQuantity(quantities, value, Number.isFinite(quantity) ? quantity : 1);
      continue;
    }

    if (key.startsWith('updates[') && key.endsWith(']')) {
      const id = key.slice('updates['.length, -1);

      addQuantity(quantities, id, Number(value) || 0);
    }
  }

  if (line != null && bareQuantity != null && !quantities.has(`line:${line}`)) {
    addQuantity(quantities, `line:${line}`, bareQuantity);
  }

  return { quantities, line };
}

/**
 * @typedef {Object} CartLine
 * @property {number} line
 * @property {string} key
 * @property {string} variantId
 * @property {number} quantity
 * @property {boolean} isSab
 */

/**
 * @typedef {Object} CartState
 * @property {boolean} hasSab
 * @property {boolean} hasOther
 * @property {CartLine[]} lines
 */

/** @type {CartState | null} */
let cartState = null;

/** Set after the first local read so later checks do not wait on /cart.js. */
let cartStateReady = false;

/**
 * @param {CartLine[]} lines
 * @returns {CartState}
 */
function cartStateFromLines(lines) {
  return {
    hasSab: lines.some((line) => line.isSab),
    hasOther: lines.some((line) => !line.isSab),
    lines,
  };
}

/**
 * @param {{ variant_id?: unknown, variantId?: unknown, key?: unknown, quantity?: unknown, sku?: unknown }[]} items
 * @returns {CartState}
 */
function cartStateFromItems(items) {
  /** @type {CartLine[]} */
  const lines = [];

  for (const [index, item] of items.entries()) {
    const variantId = String(item.variant_id ?? item.variantId ?? '');
    const sku = typeof item.sku === 'string' ? item.sku : '';

    if (!variantId) continue;

    skuByVariantId.set(variantId, sku);
    lines.push({
      line: index + 1,
      key: String(item.key ?? ''),
      variantId,
      quantity: Number(item.quantity) || 0,
      isSab: isSabSku(sku),
    });
  }

  return cartStateFromLines(lines);
}

/**
 * Cart rows already on the page. Null when this page has no cart component.
 * @returns {CartState | null}
 */
function readDomCart() {
  const root = document.querySelector('cart-items-component');

  if (!(root instanceof HTMLElement)) return null;

  /** @type {CartLine[]} */
  const lines = [];

  for (const row of root.querySelectorAll('[data-cart-line][data-variant-id]')) {
    if (!(row instanceof HTMLElement)) continue;

    const line = Number(row.dataset.cartLine);
    const variantId = row.dataset.variantId || '';
    const sku = row.dataset.sku || '';

    if (!variantId || !Number.isFinite(line)) continue;

    skuByVariantId.set(variantId, sku);
    lines.push({
      line,
      key: row.dataset.key || '',
      variantId,
      quantity: Number(row.dataset.quantity) || 0,
      isSab: isSabSku(sku),
    });
  }

  lines.sort((left, right) => left.line - right.line);

  return cartStateFromLines(lines);
}

/**
 * @returns {CartState | null}
 */
function readSeededCart() {
  const seeded = themeConfig()?.sabCart;

  if (!Array.isArray(seeded)) return null;

  return cartStateFromItems(seeded);
}

/**
 * @returns {CartState}
 */
function readCartState() {
  if (cartStateReady && cartState) return cartState;

  cartState = readDomCart() || readSeededCart() || cartStateFromLines([]);
  cartStateReady = true;

  return cartState;
}

/**
 * @param {string} variantId
 * @returns {string | null}
 */
function skuFromPage(variantId) {
  if (skuByVariantId.has(variantId)) return skuByVariantId.get(variantId) || '';

  for (const node of document.querySelectorAll('[data-variant-id][data-sku], input[name="id"][data-sku]')) {
    if (!(node instanceof HTMLElement)) continue;

    const id = node.dataset.variantId || (node instanceof HTMLInputElement ? node.value : '');

    if (id !== variantId || typeof node.dataset.sku !== 'string') continue;

    skuByVariantId.set(variantId, node.dataset.sku);

    return node.dataset.sku;
  }

  return null;
}

/**
 * @param {string} variantId
 * @returns {Promise<string | null>}
 */
async function skuForVariant(variantId) {
  const known = skuFromPage(variantId);

  if (known != null) return known;

  const root = window.Shopify?.routes?.root || '/';
  const prefix = root.endsWith('/') ? root : `${root}/`;
  const response = await originalFetch(`${prefix}variants/${variantId}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) return null;

  const data = await response.json();
  const sku = typeof data.sku === 'string' ? data.sku : '';

  skuByVariantId.set(variantId, sku);

  return sku;
}

/**
 * Keep the in-memory cart aligned with a cart mutation response so the next
 * check does not request /cart.js.
 * @param {unknown} data
 * @param {'add' | 'update' | 'change'} kind
 */
function rememberCartResponse(data, kind) {
  if (!data || typeof data !== 'object') return;

  if (Array.isArray(data.items) && typeof data.item_count === 'number') {
    cartState = cartStateFromItems(data.items);
    cartStateReady = true;

    return;
  }

  if (kind !== 'add' || !cartState) return;

  const added = Array.isArray(data.items) ? data.items : 'variant_id' in data ? [data] : [];

  for (const item of added) {
    if (!item || item.variant_id == null) continue;

    const variantId = String(item.variant_id);
    const quantity = Number(item.quantity) || 0;
    const sku = typeof item.sku === 'string' ? item.sku : skuByVariantId.get(variantId) || '';
    const existing = cartState.lines.find((line) => line.variantId === variantId);

    if (sku) skuByVariantId.set(variantId, sku);

    if (existing) {
      existing.quantity += quantity;
    } else if (quantity > 0) {
      cartState.lines.push({
        line: cartState.lines.length + 1,
        key: String(item.key || ''),
        variantId,
        quantity,
        isSab: isSabSku(sku),
      });
    }
  }

  cartState = cartStateFromLines(cartState.lines);
}

/**
 * @param {CartState} cartState
 * @param {string} id
 * @returns {CartLine | null}
 */
function lineForId(cartState, id) {
  return cartState.lines.find((item) => item.variantId === id || item.key === id) || null;
}

/**
 * Cart/add increases an existing line. Cart/change and cart/update set the quantity.
 * @param {IncomingCartChange} incoming
 * @param {CartState} cartState
 * @param {'add' | 'update' | 'change'} kind
 * @returns {Promise<'mix' | 'quantity' | 'another' | null>}
 */
async function sabViolation(incoming, cartState, kind) {
  if (incoming.line != null) {
    const line = cartState.lines.find((item) => item.line === incoming.line);
    const nextQuantity = incoming.quantities.get(`line:${incoming.line}`);

    if (line?.isSab && nextQuantity != null && nextQuantity > 1 && nextQuantity > line.quantity) return 'quantity';

    return null;
  }

  /** @type {{ id: string, quantity: number, existing: CartLine | null }[]} */
  const changes = [];
  /** @type {string[]} */
  const unresolvedIds = [];

  for (const [id, quantity] of incoming.quantities) {
    if (id.startsWith('line:')) continue;

    const existing = lineForId(cartState, id);

    changes.push({ id, quantity, existing });

    if (!existing && quantity > 0 && /^\d+$/.test(id)) unresolvedIds.push(id);
  }

  /** @type {Map<string, string | null>} */
  const skuById = new Map();

  if (unresolvedIds.length > 0) {
    const skus = await Promise.all(unresolvedIds.map((id) => skuForVariant(id)));

    unresolvedIds.forEach((id, index) => {
      skuById.set(id, skus[index] ?? null);
    });
  }

  let addsSab = false;
  let addsOther = false;
  let sabAddCount = 0;
  let raisesQuantity = false;

  for (const change of changes) {
    if (change.existing) {
      const increasesExisting =
        change.existing.isSab &&
        ((kind === 'add' && change.quantity > 0) || (kind !== 'add' && change.quantity > change.existing.quantity));

      if (increasesExisting) raisesQuantity = true;
      continue;
    }

    if (change.quantity <= 0 || !/^\d+$/.test(change.id)) continue;

    const sku = skuById.get(change.id);

    if (sku == null) continue;

    if (isSabSku(sku)) {
      addsSab = true;
      sabAddCount += 1;
      if (change.quantity > 1) raisesQuantity = true;
    } else {
      addsOther = true;
    }
  }

  const mixes =
    (addsSab && addsOther) ||
    (cartState.hasSab && addsOther) ||
    (cartState.hasOther && addsSab) ||
    (cartState.hasSab && cartState.hasOther && (addsSab || addsOther));

  if (mixes) return 'mix';
  if ((cartState.hasSab && addsSab) || sabAddCount > 1) return 'another';
  if (raisesQuantity) return 'quantity';

  return null;
}

/**
 * @param {string} message
 * @returns {Response}
 */
function blockedResponse(message) {
  return new Response(
    JSON.stringify({
      status: 422,
      message,
      description: message,
      errors: message,
    }),
    {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

function clearInlineNotices() {
  for (const note of document.querySelectorAll('[data-sab-inline="true"]')) {
    note.remove();
  }
}

/**
 * Quick-add cards have no add-to-cart error slot. Product pages already
 * render the message from the 422 body.
 * @param {string} message
 */
function announceBlockedAdd(message) {
  const form = Date.now() - lastAddFormAt < 3000 ? lastAddForm : null;

  if (form?.querySelector('[ref="addToCartTextError"]')) return;

  const host = form?.closest('product-form-component') || form;

  if (typeof HTMLElement === 'undefined' || !(host instanceof HTMLElement)) return;

  let note = host.querySelector('[data-sab-inline="true"]');

  if (!(note instanceof HTMLElement)) {
    note = document.createElement('p');
    note.className = 'cart-sab-exclusive-notice';
    note.dataset.sabInline = 'true';
    note.setAttribute('role', 'alert');
    host.prepend(note);
  }

  note.textContent = message;
}

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit | undefined} init
 * @param {'add' | 'update' | 'change'} kind
 * @returns {Promise<Response>}
 */
async function guardCartMutation(input, init, kind) {
  let request;

  try {
    request = new Request(input, init);
  } catch (error) {
    console.error('Send-a-balloon cart check could not read the request.', error);

    return originalFetch(input, init);
  }

  try {
    const incoming = await readIncomingChange(request.clone());
    const state = readCartState();
    const violation = await sabViolation(incoming, state, kind);

    if (violation) {
      const message = blockMessage(violation);

      if (kind === 'add') {
        try {
          announceBlockedAdd(message);
        } catch (error) {
          console.error('Send-a-balloon notice could not be shown.', error);
        }
      }

      return blockedResponse(message);
    }
  } catch (error) {
    console.error('Send-a-balloon cart check failed.', error);
  }

  clearInlineNotices();

  const response = await originalFetch(request);

  try {
    rememberCartResponse(await response.clone().json(), kind);
  } catch (error) {
    console.error('Send-a-balloon cart state could not be updated.', error);
  }

  return response;
}

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
function fetchWithSabGuard(input, init) {
  const kind = cartMutationKind(requestUrl(input));
  const method = requestMethod(input, init);

  if (!kind || method !== 'POST') {
    return originalFetch(input, init);
  }

  const run = gate.then(() => guardCartMutation(input, init, kind));

  gate = run.then(
    () => {},
    () => {}
  );

  return run;
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isMixedCartCheckout(event) {
  if (!document.querySelector('[data-sab-mixed="true"]')) return false;

  const target = event.target;

  if (!(target instanceof Element)) return false;

  return Boolean(
    target.closest(
      'button.cart__checkout-button, .wcp_checkout_btn, .additional-checkout-buttons, shopify-accelerated-checkout'
    )
  );
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || form.action || '';

    if (/\/cart\/add(\.js)?/.test(action)) {
      lastAddForm = form;
      lastAddFormAt = Date.now();
    }

    if (!document.querySelector('[data-sab-mixed="true"]')) return;
    if (!/\/checkout/.test(action) && !form.querySelector('[name="checkout"]')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    if (!isMixedCartCheckout(event)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

window.fetch = fetchWithSabGuard;
